import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { Input } from '@/shared/components/Input';
import { Spinner } from '@/shared/components/Spinner';
import { cn } from '@/shared/helpers/cn';
import styles from './AsyncAutocomplete.module.scss';

/** Estado explicito da busca — cada um tem mensagem propria na tela. */
export type AsyncSearchStatus = 'idle' | 'typing' | 'searching' | 'empty' | 'error' | 'ready';

export interface AsyncAutocompleteProps<T> {
  label: string;
  value: T | null;
  onSelect: (item: T | null) => void;
  /** Executa a busca. Recebe um `AbortSignal` para cancelar a requisicao anterior. */
  search: (term: string, signal: AbortSignal) => Promise<T[]>;
  itemKey: (item: T) => string;
  itemLabel: (item: T) => string;
  /** Conteudo da linha na lista. Padrao: o rotulo. */
  renderItem?: (item: T) => ReactNode;
  /** Minimo de caracteres antes de buscar. */
  minChars?: number;
  debounceMs?: number;
  placeholder?: string;
  /**
   * Acao mostrada quando a busca nao encontra nada — ex.: "Novo paciente".
   *
   * Como funcao, recebe o termo que nao encontrou nada: e o que permite oferecer
   * "Cadastrar 'Joana Ribeiro'" em vez de um "Novo paciente" generico, e abrir o
   * cadastro com o nome ja preenchido.
   */
  emptyAction?: ReactNode | ((term: string) => ReactNode);
  disabled?: boolean;
  className?: string;
}

/**
 * Busca assincrona com selecao.
 *
 * Tres cuidados que o legado nao tinha: **debounce** (uma requisicao por pausa,
 * nao por tecla), **cancelamento da requisicao anterior** via `AbortController`
 * (sem isso, uma resposta lenta sobrescreve uma rapida e a lista "volta no
 * tempo"), e **estado explicito** para digitando / buscando / sem resultado /
 * erro, em vez de uma lista vazia ambigua.
 *
 * Segue o padrao ARIA de combobox: navegacao por setas, `Enter` seleciona,
 * `Escape` fecha, e a opcao ativa e anunciada por `aria-activedescendant`.
 */
export function AsyncAutocomplete<T>({
  label,
  value,
  onSelect,
  search,
  itemKey,
  itemLabel,
  renderItem,
  minChars = 3,
  debounceMs = 300,
  placeholder = 'Digite para buscar...',
  emptyAction,
  disabled,
  className,
}: AsyncAutocompleteProps<T>) {
  const listId = useId();
  const [term, setTerm] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [status, setStatus] = useState<AsyncSearchStatus>('idle');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const trimmed = term.trim();

    if (trimmed.length < minChars) {
      abortRef.current?.abort();
      setItems([]);
      setStatus(trimmed.length === 0 ? 'idle' : 'typing');
      return;
    }

    setStatus('typing');
    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setStatus('searching');
      search(trimmed, controller.signal)
        .then((results) => {
          if (controller.signal.aborted) return;
          setItems(results);
          setStatus(results.length === 0 ? 'empty' : 'ready');
          setActiveIndex(results.length > 0 ? 0 : -1);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setItems([]);
          setStatus('error');
        });
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [term, minChars, debounceMs, search]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const choose = (item: T): void => {
    onSelect(item);
    setTerm('');
    setItems([]);
    setStatus('idle');
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && open && activeIndex >= 0) {
      event.preventDefault();
      const item = items[activeIndex];
      if (item) choose(item);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  if (value) {
    return (
      <div className={cn(styles.root, className)}>
        <div className={styles.selected}>
          <span className={styles.selectedLabel}>{itemLabel(value)}</span>
          <button
            type="button"
            className={styles.remove}
            onClick={() => onSelect(null)}
            disabled={disabled}
            aria-label={`Remover ${itemLabel(value)}`}
          >
            <Icon as={X} size={16} />
          </button>
        </div>
      </div>
    );
  }

  const showList = open && (status === 'ready' || status === 'empty' || status === 'error');

  return (
    <div className={cn(styles.root, className)}>
      <Input
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={label}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        placeholder={placeholder}
        value={term}
        disabled={disabled}
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        suffix={status === 'searching' ? <Spinner size={14} /> : null}
      />

      <p className={styles.status} aria-live="polite">
        {status === 'typing' && term.trim().length > 0 && term.trim().length < minChars
          ? `Digite ao menos ${minChars} caracteres`
          : status === 'searching'
            ? 'Buscando...'
            : status === 'ready'
              ? `${items.length} resultado${items.length > 1 ? 's' : ''}`
              : ''}
      </p>

      {showList ? (
        <ul className={styles.list} id={listId} role="listbox" aria-label={label}>
          {status === 'error' ? (
            <li className={styles.message}>Não foi possível buscar agora. Tente novamente.</li>
          ) : status === 'empty' ? (
            <li className={styles.message}>
              <span>Nenhum resultado para “{term.trim()}”.</span>
              {emptyAction ? (
                <span className={styles.emptyAction}>
                  {typeof emptyAction === 'function' ? emptyAction(term.trim()) : emptyAction}
                </span>
              ) : null}
            </li>
          ) : (
            items.map((item, index) => (
              <li
                key={itemKey(item)}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={cn(styles.item, index === activeIndex && styles.active)}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => {
                  // `mousedown` roda antes do blur do input, que fecharia a lista.
                  event.preventDefault();
                  choose(item);
                }}
              >
                {renderItem ? renderItem(item) : itemLabel(item)}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
